import { z } from 'zod';
import { PAYMENT_METHODS } from './collections';
import { CUSTOMER_KINDS, IDENTITY_TYPES } from './crm';
import { BusinessCodeSchema, PhoneSchema } from './identifiers';
import { FINISHING_STATUSES, PROPERTY_TYPES, USAGE_TYPES } from './inventory';
import type { Permission } from './authorization';
import type { LocalizedLabel } from './localized';
import { MoneySchema } from './money';
import { INSTALLMENT_KINDS } from './sales';
import { BusinessDateSchema, InstantSchema, type BusinessDate } from './time';

/**
 * Client onboarding imports (Release 1, CORE-IMPORT-001/002, BD-63).
 *
 * One catalogue drives everything a person and the server see: the downloadable templates and their
 * instructions, the column mapping, the cell parsers, and the import centre's help text. A column
 * added here appears in the template, the mapping and the validation together, or not at all.
 *
 * **Nothing is guessed.** Dates are `YYYY-MM-DD` only (never `03/04/2025`, which is two different
 * days depending on who wrote it). Amounts are plain decimal text with at most two decimals — no
 * thousands separators, no currency symbols, never a binary floating-point value. Phone numbers,
 * identity numbers and codes are text: a spreadsheet cell holding them as a number has already lost
 * its leading zero, so such a cell is refused rather than repaired.
 */

export const ONBOARDING_IMPORT_KINDS = [
  'customers',
  'units',
  'contracts',
  'installments',
  'receipts',
] as const;
export type OnboardingImportKind = (typeof ONBOARDING_IMPORT_KINDS)[number];

export function isOnboardingKind(kind: string): kind is OnboardingImportKind {
  return (ONBOARDING_IMPORT_KINDS as readonly string[]).includes(kind);
}

/** A row whose source identifier starts with this is a template example, and is always refused. */
export const EXAMPLE_SOURCE_PREFIX = 'EXAMPLE-';

export const IMPORT_COLUMN_FORMATS = [
  /** An identifier in the source system: letters, digits, `. _ / -`, up to 80. */
  'sourceId',
  /** A business code already in this system (branch, project, building, unit). */
  'code',
  'text',
  'phone',
  'identity',
  'email',
  /** `YYYY-MM-DD`. */
  'date',
  /** Decimal text, at most two decimals, greater than zero. */
  'money',
  /** Decimal text, at most four decimals, greater than zero. */
  'decimal',
  'integer',
  /** One of the listed values, exactly. */
  'enum',
  /** ISO 4217, three upper-case letters. */
  'currency',
  /** A person's sign-in identifier (their e-mail) in this system. */
  'login',
] as const;
export type ImportColumnFormat = (typeof IMPORT_COLUMN_FORMATS)[number];

export interface ImportColumnSpec {
  name: string;
  required: boolean;
  format: ImportColumnFormat;
  values?: readonly string[];
  /**
   * Columns sharing a group are alternatives: exactly one of them must be filled on each row (a
   * contract names its customer by source identifier **or** by phone).
   */
  oneOf?: string;
  label: LocalizedLabel;
  hint: LocalizedLabel;
}

const col = (
  name: string,
  required: boolean,
  format: ImportColumnFormat,
  label: LocalizedLabel,
  hint: LocalizedLabel,
  extra: Partial<Pick<ImportColumnSpec, 'values' | 'oneOf'>> = {},
): ImportColumnSpec => ({ name, required, format, label, hint, ...extra });

const DATE_HINT = {
  ar: 'التاريخ بصيغة YYYY-MM-DD مثل 2025-03-31',
  en: 'Date as YYYY-MM-DD, e.g. 2025-03-31',
};
const MONEY_HINT = {
  ar: 'رقم عشري بدون فواصل آلاف أو رمز عملة، بحد أقصى منزلتين عشريتين',
  en: 'Decimal number without thousands separators or currency symbol, at most two decimals',
};
const TEXT_ID_HINT = {
  ar: 'نص — اجعل تنسيق العمود «نص» حتى لا تُحذف الأصفار في البداية',
  en: 'Text — format the column as Text so leading zeros are kept',
};

export const IMPORT_COLUMN_SPECS: Readonly<
  Record<OnboardingImportKind, readonly ImportColumnSpec[]>
> = {
  customers: [
    col(
      'customer_source_id',
      true,
      'sourceId',
      { ar: 'معرّف العميل في المصدر', en: 'Customer source ID' },
      {
        ar: 'معرّف ثابت للعميل في نظامكم السابق؛ يُستخدم للربط ومنع التكرار',
        en: 'Stable identifier of the customer in your previous system; used for linking and deduplication',
      },
    ),
    col(
      'branch_code',
      true,
      'code',
      { ar: 'كود الفرع', en: 'Branch code' },
      {
        ar: 'كود فرع موجود في الهيكل التنظيمي',
        en: 'Code of an existing branch in the organization',
      },
    ),
    col(
      'name',
      true,
      'text',
      { ar: 'الاسم', en: 'Name' },
      {
        ar: 'كما هو مكتوب، من 2 إلى 160 حرفًا',
        en: 'As written, 2 to 160 characters',
      },
    ),
    col(
      'alternate_name',
      false,
      'text',
      { ar: 'الاسم البديل', en: 'Alternate name' },
      {
        ar: 'الاسم باللغة الأخرى إن وجد',
        en: 'The name in the other language, if any',
      },
    ),
    col(
      'kind',
      false,
      'enum',
      { ar: 'النوع', en: 'Kind' },
      {
        ar: 'individual أو company — الافتراضي individual',
        en: 'individual or company — default individual',
      },
      { values: CUSTOMER_KINDS },
    ),
    col(
      'primary_phone',
      true,
      'phone',
      { ar: 'الهاتف الأساسي', en: 'Primary phone' },
      TEXT_ID_HINT,
    ),
    col(
      'secondary_phone',
      false,
      'phone',
      { ar: 'هاتف إضافي', en: 'Secondary phone' },
      TEXT_ID_HINT,
    ),
    col(
      'email',
      false,
      'email',
      { ar: 'البريد الإلكتروني', en: 'E-mail' },
      {
        ar: 'عنوان بريد صحيح',
        en: 'A valid e-mail address',
      },
    ),
    col(
      'identity_type',
      false,
      'enum',
      { ar: 'نوع الهوية', en: 'Identity type' },
      {
        ar: 'nationalId أو passport أو commercialRegistration أو taxNumber',
        en: 'nationalId, passport, commercialRegistration or taxNumber',
      },
      { values: IDENTITY_TYPES },
    ),
    col(
      'identity_number',
      false,
      'identity',
      { ar: 'رقم الهوية', en: 'Identity number' },
      TEXT_ID_HINT,
    ),
    col(
      'identity_country',
      false,
      'text',
      { ar: 'دولة الإصدار', en: 'Issuing country' },
      {
        ar: 'رمز الدولة من حرفين مثل EG (لجواز السفر)',
        en: 'Two-letter country code such as EG (for a passport)',
      },
    ),
    col(
      'address',
      false,
      'text',
      { ar: 'العنوان', en: 'Address' },
      { ar: 'حتى 400 حرف', en: 'Up to 400 characters' },
    ),
    col(
      'city',
      false,
      'text',
      { ar: 'المدينة', en: 'City' },
      { ar: 'حتى 80 حرفًا', en: 'Up to 80 characters' },
    ),
    col(
      'preferred_language',
      false,
      'enum',
      { ar: 'اللغة المفضلة', en: 'Preferred language' },
      {
        ar: 'ar أو en',
        en: 'ar or en',
      },
      { values: ['ar', 'en'] },
    ),
    col(
      'owner_login',
      false,
      'login',
      { ar: 'مسؤول العميل', en: 'Customer owner' },
      {
        ar: 'بريد الدخول لموظف نشط في الفرع؛ فارغ = من يستورد الملف',
        en: 'Sign-in e-mail of an active employee in the branch; empty = the person importing',
      },
    ),
  ],
  units: [
    col(
      'project_code',
      true,
      'code',
      { ar: 'كود المشروع', en: 'Project code' },
      {
        ar: 'مشروع موجود — يُنشأ أولًا من شاشة المشروعات',
        en: 'An existing project — create it first on the Projects screen',
      },
    ),
    col(
      'building_code',
      true,
      'code',
      { ar: 'كود المبنى', en: 'Building code' },
      {
        ar: 'مبنى موجود داخل المشروع — يُنشأ أولًا من شاشة المشروع',
        en: 'An existing building in the project — create it first on the project screen',
      },
    ),
    col(
      'unit_code',
      true,
      'code',
      { ar: 'كود الوحدة', en: 'Unit code' },
      {
        ar: 'حروف إنجليزية كبيرة وأرقام و - و / ؛ فريد داخل المشروع',
        en: 'Upper-case letters, digits, - and /; unique within the project',
      },
    ),
    col(
      'floor',
      true,
      'integer',
      { ar: 'الدور', en: 'Floor' },
      {
        ar: 'رقم صحيح من -5 حتى عدد أدوار المبنى',
        en: 'Whole number from -5 up to the building’s floors',
      },
    ),
    col(
      'property_type',
      true,
      'enum',
      { ar: 'نوع العقار', en: 'Property type' },
      {
        ar: PROPERTY_TYPES.join(' / '),
        en: PROPERTY_TYPES.join(' / '),
      },
      { values: PROPERTY_TYPES },
    ),
    col(
      'usage_type',
      true,
      'enum',
      { ar: 'الاستخدام', en: 'Usage' },
      {
        ar: USAGE_TYPES.join(' / '),
        en: USAGE_TYPES.join(' / '),
      },
      { values: USAGE_TYPES },
    ),
    col(
      'area',
      true,
      'decimal',
      { ar: 'المساحة م²', en: 'Area m²' },
      {
        ar: 'رقم عشري حتى أربع منازل',
        en: 'Decimal, up to four decimals',
      },
    ),
    col('base_price', true, 'money', { ar: 'السعر الأساسي', en: 'Base price' }, MONEY_HINT),
    col(
      'current_price',
      false,
      'money',
      { ar: 'السعر الحالي', en: 'Current price' },
      {
        ar: 'فارغ = السعر الأساسي',
        en: 'Empty = the base price',
      },
    ),
    col(
      'currency',
      false,
      'currency',
      { ar: 'العملة', en: 'Currency' },
      {
        ar: 'إن وُجدت يجب أن تطابق عملة المشروع مثل EGP',
        en: 'If given, must equal the project currency, e.g. EGP',
      },
    ),
    col(
      'finishing_status',
      true,
      'enum',
      { ar: 'التشطيب', en: 'Finishing' },
      {
        ar: FINISHING_STATUSES.join(' / '),
        en: FINISHING_STATUSES.join(' / '),
      },
      { values: FINISHING_STATUSES },
    ),
    col(
      'bedrooms',
      false,
      'integer',
      { ar: 'غرف النوم', en: 'Bedrooms' },
      { ar: '0 حتى 50', en: '0 to 50' },
    ),
    col(
      'bathrooms',
      false,
      'integer',
      { ar: 'الحمامات', en: 'Bathrooms' },
      { ar: '0 حتى 50', en: '0 to 50' },
    ),
    col(
      'parking_spaces',
      false,
      'integer',
      { ar: 'مواقف السيارات', en: 'Parking spaces' },
      { ar: '0 حتى 50', en: '0 to 50' },
    ),
    col(
      'storage_rooms',
      false,
      'integer',
      { ar: 'المخازن', en: 'Storage rooms' },
      { ar: '0 حتى 50', en: '0 to 50' },
    ),
    col(
      'garden_area',
      false,
      'decimal',
      { ar: 'مساحة الحديقة', en: 'Garden area' },
      { ar: 'رقم عشري', en: 'Decimal' },
    ),
    col(
      'roof_area',
      false,
      'decimal',
      { ar: 'مساحة السطح', en: 'Roof area' },
      { ar: 'رقم عشري', en: 'Decimal' },
    ),
    col(
      'view_ar',
      false,
      'text',
      { ar: 'الإطلالة (عربي)', en: 'View (Arabic)' },
      {
        ar: 'تُملأ مع view_en معًا',
        en: 'Filled together with view_en',
      },
    ),
    col(
      'view_en',
      false,
      'text',
      { ar: 'الإطلالة (إنجليزي)', en: 'View (English)' },
      {
        ar: 'تُملأ مع view_ar معًا',
        en: 'Filled together with view_ar',
      },
    ),
  ],
  contracts: [
    col(
      'contract_source_id',
      true,
      'sourceId',
      { ar: 'معرّف العقد في المصدر', en: 'Contract source ID' },
      {
        ar: 'معرّف ثابت للعقد؛ يربط ملف الأقساط والمقبوضات بالعقد',
        en: 'Stable contract identifier; links the schedule and receipts files to the contract',
      },
    ),
    col(
      'customer_source_id',
      false,
      'sourceId',
      { ar: 'معرّف العميل في المصدر', en: 'Customer source ID' },
      {
        ar: 'عميل مستورد بنفس المصدر — أو استخدم customer_phone',
        en: 'A customer imported from the same source — or use customer_phone',
      },
      { oneOf: 'customer' },
    ),
    col(
      'customer_phone',
      false,
      'phone',
      { ar: 'هاتف العميل', en: 'Customer phone' },
      {
        ar: 'الهاتف الأساسي لعميل موجود في نفس الكيان القانوني',
        en: 'Primary phone of an existing customer in the same legal entity',
      },
      { oneOf: 'customer' },
    ),
    col(
      'project_code',
      true,
      'code',
      { ar: 'كود المشروع', en: 'Project code' },
      {
        ar: 'مشروع موجود',
        en: 'An existing project',
      },
    ),
    col(
      'unit_code',
      true,
      'code',
      { ar: 'كود الوحدة', en: 'Unit code' },
      {
        ar: 'وحدة متاحة في المشروع؛ تصبح «متعاقد عليها» عند استيراد الجدول',
        en: 'An available unit in the project; it becomes contracted when the schedule is imported',
      },
    ),
    col(
      'contracted_on',
      true,
      'date',
      { ar: 'تاريخ التعاقد', en: 'Contract date' },
      {
        ar: 'قبل تاريخ بدء التشغيل (cutover)، بصيغة YYYY-MM-DD',
        en: 'Before the cutover date, as YYYY-MM-DD',
      },
    ),
    col(
      'unit_price',
      true,
      'money',
      { ar: 'سعر الوحدة المتفق عليه', en: 'Agreed unit price' },
      MONEY_HINT,
    ),
    col(
      'maintenance_deposit',
      false,
      'money',
      { ar: 'وديعة الصيانة', en: 'Maintenance deposit' },
      {
        ar: 'تضاف إلى قيمة العقد؛ يجب أن تساويها أقساط النوع maintenanceDeposit',
        en: 'Added to the contract value; maintenanceDeposit schedule rows must equal it',
      },
    ),
    col(
      'currency',
      false,
      'currency',
      { ar: 'العملة', en: 'Currency' },
      {
        ar: 'إن وُجدت يجب أن تطابق عملة المشروع',
        en: 'If given, must equal the project currency',
      },
    ),
    col(
      'original_contract_number',
      false,
      'text',
      { ar: 'رقم العقد الأصلي', en: 'Original contract number' },
      {
        ar: 'رقم العقد الورقي — يُحفظ كما هو ويظهر على العقد',
        en: 'The paper contract number — kept as entered and shown on the contract',
      },
    ),
    col(
      'sales_owner_login',
      false,
      'login',
      { ar: 'مسؤول البيع', en: 'Sales owner' },
      {
        ar: 'بريد الدخول لموظف نشط؛ فارغ = من يستورد الملف',
        en: 'Sign-in e-mail of an active employee; empty = the person importing',
      },
    ),
  ],
  installments: [
    col(
      'contract_source_id',
      true,
      'sourceId',
      { ar: 'معرّف العقد في المصدر', en: 'Contract source ID' },
      {
        ar: 'عقد تم التحقق منه في ملف العقود وينتظر جدوله',
        en: 'A contract validated in the contracts file and waiting for its schedule',
      },
    ),
    col(
      'sequence',
      true,
      'integer',
      { ar: 'رقم القسط', en: 'Sequence' },
      {
        ar: '1، 2، 3 ... متتالية لكل عقد',
        en: '1, 2, 3 … consecutive for each contract',
      },
    ),
    col('due_on', true, 'date', { ar: 'تاريخ الاستحقاق', en: 'Due date' }, DATE_HINT),
    col(
      'amount',
      true,
      'money',
      { ar: 'قيمة القسط', en: 'Amount' },
      {
        ar: 'مجموع الأقساط يجب أن يساوي قيمة العقد تمامًا',
        en: 'The rows must add up to the contract value exactly',
      },
    ),
    col(
      'kind',
      false,
      'enum',
      { ar: 'نوع القسط', en: 'Kind' },
      {
        ar: `${INSTALLMENT_KINDS.join(' / ')} — الافتراضي installment`,
        en: `${INSTALLMENT_KINDS.join(' / ')} — default installment`,
      },
      { values: INSTALLMENT_KINDS },
    ),
    col(
      'label',
      false,
      'text',
      { ar: 'الوصف', en: 'Label' },
      { ar: 'حتى 120 حرفًا', en: 'Up to 120 characters' },
    ),
  ],
  receipts: [
    col(
      'receipt_source_id',
      true,
      'sourceId',
      { ar: 'معرّف الإيصال في المصدر', en: 'Receipt source ID' },
      {
        ar: 'معرّف ثابت للإيصال؛ يمنع تسجيل نفس المبلغ مرتين',
        en: 'Stable receipt identifier; prevents recording the same money twice',
      },
    ),
    col(
      'contract_source_id',
      false,
      'sourceId',
      { ar: 'معرّف العقد في المصدر', en: 'Contract source ID' },
      {
        ar: 'عقد مستورد — أو استخدم contract_number',
        en: 'An imported contract — or use contract_number',
      },
      { oneOf: 'contract' },
    ),
    col(
      'contract_number',
      false,
      'text',
      { ar: 'رقم العقد في النظام', en: 'Contract number' },
      {
        ar: 'رقم العقد كما يظهر في النظام لعقد مستورد',
        en: 'The system contract number of an imported contract',
      },
      { oneOf: 'contract' },
    ),
    col(
      'received_on',
      true,
      'date',
      { ar: 'تاريخ القبض', en: 'Received on' },
      {
        ar: 'قبل تاريخ بدء التشغيل (cutover)؛ المقبوضات بعده تُسجل من شاشة التحصيل',
        en: 'Before the cutover date; money received later is recorded on the collections screen',
      },
    ),
    col('amount', true, 'money', { ar: 'المبلغ', en: 'Amount' }, MONEY_HINT),
    col(
      'method',
      true,
      'enum',
      { ar: 'طريقة الدفع', en: 'Method' },
      {
        ar: PAYMENT_METHODS.join(' / '),
        en: PAYMENT_METHODS.join(' / '),
      },
      { values: PAYMENT_METHODS },
    ),
    col(
      'installment_sequence',
      false,
      'integer',
      { ar: 'رقم القسط المسدد', en: 'Installment sequence' },
      {
        ar: 'يُخصص المبلغ كله لهذا القسط؛ فارغ = الأقدم فالأحدث',
        en: 'The whole amount goes to this instalment; empty = oldest first',
      },
    ),
    col(
      'original_receipt_number',
      false,
      'text',
      { ar: 'رقم الإيصال الأصلي', en: 'Original receipt number' },
      {
        ar: 'رقم الإيصال الورقي — يُحفظ كما هو',
        en: 'The paper receipt number — kept as entered',
      },
    ),
    col(
      'reference',
      false,
      'text',
      { ar: 'مرجع التحويل أو الشيك', en: 'Transfer or cheque reference' },
      {
        ar: 'حتى 120 حرفًا',
        en: 'Up to 120 characters',
      },
    ),
    col(
      'notes',
      false,
      'text',
      { ar: 'ملاحظات', en: 'Notes' },
      { ar: 'حتى 2000 حرف', en: 'Up to 2000 characters' },
    ),
  ],
};

/** What each kind needs to exist first, for the import centre and the template's instructions sheet. */
export const IMPORT_PREREQUISITES: Readonly<Record<OnboardingImportKind, LocalizedLabel>> = {
  customers: {
    ar: 'الفروع موجودة في الهيكل التنظيمي.',
    en: 'Branches exist in the organization.',
  },
  units: {
    ar: 'المشروعات والمباني تُنشأ أولًا من شاشاتها؛ الاستيراد لا ينشئها.',
    en: 'Projects and buildings are created first on their screens; the import never creates them.',
  },
  contracts: {
    ar: 'العملاء والوحدات مستوردة أو موجودة، وتاريخ بدء التشغيل محدد. ملف العقود يُتحقق منه ويُحفظ بانتظار جدول الأقساط؛ لا يُنشأ عقد بدون جدوله.',
    en: 'Customers and units exist or were imported, and the cutover date is set. The contracts file is validated and kept until its schedule is imported; no contract exists without its schedule.',
  },
  installments: {
    ar: 'ملف العقود تم اعتماده (العقود في حالة «بانتظار الجدول»). كل عقد ينشأ نشطًا مع جدوله في عملية واحدة.',
    en: 'The contracts file was committed (contracts awaiting their schedule). Each contract is created active with its schedule in one step.',
  },
  receipts: {
    ar: 'العقود مستوردة بجداولها. هذه مقبوضات قبل تاريخ بدء التشغيل: لا تحرك أي خزينة ولا تُرحّل قيدًا لأنها داخلة في الأرصدة الافتتاحية.',
    en: 'Contracts were imported with their schedules. These are receipts before the cutover date: they move no treasury balance and post no entry, because the opening balances already hold them.',
  },
};

/**
 * Fictional example rows, one set per kind. They are offered as a separate download and a separate
 * sheet — **never** in the data sheet — and every source identifier starts with `EXAMPLE-`, which the
 * importers refuse, so an example can never be imported by accident.
 */
export const IMPORT_EXAMPLES: Readonly<
  Record<OnboardingImportKind, readonly Record<string, string>[]>
> = {
  customers: [
    {
      customer_source_id: 'EXAMPLE-C-0001',
      branch_code: 'EXAMPLE-BRANCH',
      name: 'عميل تجريبي',
      kind: 'individual',
      primary_phone: '01000000000',
      email: 'example.customer@example.invalid',
      identity_type: 'nationalId',
      identity_number: '00000000000000',
      preferred_language: 'ar',
    },
    {
      customer_source_id: 'EXAMPLE-C-0002',
      branch_code: 'EXAMPLE-BRANCH',
      name: 'Example Company LLC',
      kind: 'company',
      primary_phone: '+20 100 000 0002',
      preferred_language: 'en',
    },
  ],
  units: [
    {
      project_code: 'EXAMPLE-PRJ',
      building_code: 'EXAMPLE-B1',
      unit_code: 'EXAMPLE-U-101',
      floor: '1',
      property_type: 'apartment',
      usage_type: 'residential',
      area: '145.50',
      base_price: '2500000.00',
      currency: 'EGP',
      finishing_status: 'semiFinished',
      bedrooms: '3',
      bathrooms: '2',
    },
  ],
  contracts: [
    {
      contract_source_id: 'EXAMPLE-K-0001',
      customer_source_id: 'EXAMPLE-C-0001',
      project_code: 'EXAMPLE-PRJ',
      unit_code: 'EXAMPLE-U-101',
      contracted_on: '2024-02-15',
      unit_price: '2400000.00',
      maintenance_deposit: '100000.00',
      currency: 'EGP',
      original_contract_number: 'EX/2024/0001',
    },
  ],
  installments: [
    {
      contract_source_id: 'EXAMPLE-K-0001',
      sequence: '1',
      due_on: '2024-02-15',
      amount: '400000.00',
      kind: 'downPayment',
    },
    {
      contract_source_id: 'EXAMPLE-K-0001',
      sequence: '2',
      due_on: '2024-08-15',
      amount: '1000000.00',
      kind: 'installment',
    },
    {
      contract_source_id: 'EXAMPLE-K-0001',
      sequence: '3',
      due_on: '2025-02-15',
      amount: '1000000.00',
      kind: 'finalPayment',
    },
    {
      contract_source_id: 'EXAMPLE-K-0001',
      sequence: '4',
      due_on: '2026-12-31',
      amount: '100000.00',
      kind: 'maintenanceDeposit',
    },
  ],
  receipts: [
    {
      receipt_source_id: 'EXAMPLE-R-0001',
      contract_source_id: 'EXAMPLE-K-0001',
      received_on: '2024-02-15',
      amount: '400000.00',
      method: 'bankTransfer',
      installment_sequence: '1',
      original_receipt_number: 'EX-R-0001',
      reference: 'EXAMPLE-TRANSFER-1',
    },
  ],
};

/* ------------------------------------------------------------------ cell parsing */

/** A cell problem. The import service adds the row and column. */
export type CellIssue = { code: string };
export type CellResult<T> = { ok: true; value: T } | { ok: false; issue: CellIssue };

const fail = <T>(code: string): CellResult<T> => ({ ok: false, issue: { code } });
const ok = <T>(value: T): CellResult<T> => ({ ok: true, value });

/** Only `YYYY-MM-DD`. Anything else — `31/03/2025`, `3/4/25`, `March 31` — is refused, never guessed. */
export function parseImportDate(text: string): CellResult<BusinessDate> {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) return fail('DATE_FORMAT');
  const parsed = BusinessDateSchema.safeParse(text);
  return parsed.success ? ok(parsed.data) : fail('DATE_INVALID');
}

/**
 * A positive amount with at most `scale` decimals, as decimal text. Separators, symbols, signs and
 * exponents are refused: `1,500,000` and `1.5e6` are not amounts a person meant to type into an
 * accounting import, and stripping them would be a guess.
 */
export function parseImportDecimal(
  text: string,
  scale: number,
  options: { allowZero?: boolean } = {},
): CellResult<string> {
  if (!/^\d+(\.\d+)?$/.test(text)) return fail('AMOUNT_FORMAT');
  const [whole = '', fraction = ''] = text.split('.');
  if (fraction.length > scale) return fail('AMOUNT_PRECISION');
  const normalizedWhole = whole.replace(/^0+(?=\d)/, '');
  const trimmedFraction = fraction.replace(/0+$/, '');
  const value = trimmedFraction ? `${normalizedWhole}.${trimmedFraction}` : normalizedWhole;
  if (!options.allowZero && /^0(\.0*)?$/.test(value)) return fail('AMOUNT_NOT_POSITIVE');
  if (normalizedWhole.length > 15) return fail('AMOUNT_TOO_LARGE');
  return ok(value);
}

export function parseImportInteger(text: string, min: number, max: number): CellResult<number> {
  if (!/^-?\d{1,6}$/.test(text)) return fail('INTEGER_EXPECTED');
  const value = Number(text);
  if (value < min || value > max) return fail('OUT_OF_RANGE');
  return ok(value);
}

const SOURCE_ID = /^[A-Za-z0-9][A-Za-z0-9._/-]{0,79}$/;

export function parseSourceId(text: string): CellResult<string> {
  if (!SOURCE_ID.test(text)) return fail('SOURCE_ID_FORMAT');
  if (text.toUpperCase().startsWith(EXAMPLE_SOURCE_PREFIX)) return fail('EXAMPLE_ROW');
  return ok(text);
}

export function parseImportCode(text: string): CellResult<string> {
  const parsed = BusinessCodeSchema.safeParse(text);
  return parsed.success ? ok(parsed.data) : fail('CODE_FORMAT');
}

export function parseImportPhone(text: string): CellResult<string> {
  const parsed = PhoneSchema.safeParse(text);
  return parsed.success ? ok(parsed.data) : fail('PHONE_EXPECTED');
}

export function parseImportEnum<T extends string>(
  text: string,
  values: readonly T[],
): CellResult<T> {
  return (values as readonly string[]).includes(text) ? ok(text as T) : fail('VALUE_NOT_ALLOWED');
}

/* ------------------------------------------------------------------ summary */

/**
 * What onboarding has written so far, per legal entity and currency, for the accountant to compare
 * with the opening balances (BD-63): contracts and their value, historical receipts, and the
 * receivable outstanding at cutover. Scoped like every other read.
 */
export const OnboardingSummarySchema = z.strictObject({
  cutoverDate: BusinessDateSchema.nullable(),
  rows: z.array(
    z.strictObject({
      legalEntityId: z.string(),
      currency: z.string(),
      contracts: z.number().int().nonnegative(),
      contractValue: MoneySchema,
      receipts: z.number().int().nonnegative(),
      received: MoneySchema,
      outstanding: MoneySchema,
    }),
  ),
  /** Contracts validated from a contracts file and still waiting for their schedule. */
  stagedContracts: z.number().int().nonnegative(),
  generatedAt: InstantSchema,
});
export type OnboardingSummary = z.infer<typeof OnboardingSummarySchema>;

/**
 * Who may import each kind: the import permission **and** the permission the normal workflow asks
 * for the same records. One list for the server's importers (pinned by a test) and the screens.
 */
export const ONBOARDING_IMPORT_PERMISSIONS: Readonly<
  Record<OnboardingImportKind, readonly [Permission, Permission]>
> = {
  customers: ['crm.customer.import', 'crm.customer.manage'],
  units: ['inventory.unit.import', 'inventory.unit.manage'],
  contracts: ['sales.contract.import', 'sales.contract.create'],
  installments: ['sales.contract.import', 'sales.contract.create'],
  receipts: ['collection.receipt.import', 'collection.receipt.create'],
};
